import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { ButtonChevronIcon, ScrollLeftButton, ScrollRightButton } from 'src/domains/layouts/shared/ScrollButtons.style';
import { Button } from 'src/domains/layouts/webview/components/common/button/Button';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import { withConfig } from 'src/withConfig';
import { isNewDesign } from 'src/utils/new-design';
import { getSelectionPromoTextColor, getSelectionPromoBgColor } from './HomePageCarouselPromos.helpers';
import { LazyImage } from 'src/domains/layouts/webview/components/common/lazyLoadingImage/LazyImage';
import { CheckCircleFilledIcon } from 'src/domains/layouts/shared/assets/icons/CheckCircleFilledIcon';
import { ButtonVariantType } from 'src/domains/layouts/config/features/types';

interface HomePageCarouselPromoPropsType {
    bgColor?: string;
    bgImage?: string;
    btnColor?: string;
    calcShift: number;
    isClickable?: boolean;
    isDot?: boolean;
    isHomePage?: boolean;
    isOneElem?: boolean;
    isSelected?: boolean;
    isSuspended?: boolean;
    itemsInRow?: number;
}

type HomePageCarouselPromosComponentType = Pick<HomePageCarouselPromoPropsType, 'isHomePage' | 'isDot'>;

const setOptedInBadgeWrapper = (variant: ButtonVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === ButtonVariantType.SECONDARY) {
        return css`
            border-radius: ${theme.star.radius.small};
            overflow: hidden;
        `;
    }
    return css``;
};

export const HomePageCarouselPromosComponent = withConfig(theme => styled('div', { label: 'HomePageCarouselPromosComponent' })<HomePageCarouselPromosComponentType>`
    background-color: ${({ isHomePage }): string => isHomePage === true ? theme.star.homePageCarousel.items.bgColor : theme.star.homePageCarousel.items.bgColorQuaternary};
    height: 216px;
    padding: ${({ isHomePage }): string => isHomePage === true ? '8px 0 0 8px' : '8px 0 8px 8px'};
    @media ${theme.star.mediaQuery.tablet} {
        height: ${({ isDot }): string => isDot === true ? '248px' : '216px'};
    }
`);

export const HomePageCarouselPromosListWrapper = styled('div', { label: 'HomePageCarouselPromosListWrapper' })`
    height: 100%;
    position: relative;
    width: 100%;
    overflow: hidden;
`;

type SliderWrapperType = Pick<HomePageCarouselPromoPropsType, 'calcShift'>;

export const SliderWrapper = styled('div', { label: 'SliderWrapper' })<SliderWrapperType>`
    display: flex;
    left: 0;
    position: absolute;
    top: 0;
    transition: transform 1s ease-out;
    transform: ${({ calcShift }): string => `translateX(${calcShift}px)`};
`;

type HomePageCarouselSingleItemType = Pick<HomePageCarouselPromoPropsType, 'bgColor' | 'bgImage'>;

export const HomePageCarouselSingleItem = withConfig(theme => styled('div', { label: 'HomePageCarouselSingleItem' })<HomePageCarouselSingleItemType>`
    background-color: ${({ bgColor }): string => bgColor !== undefined && bgColor !== '' ? bgColor : theme.star.homePageCarousel.items.bgColorTertiary};
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    height: 200px;
    margin-left: 8px;
    overflow: hidden;
    position: relative;
    width: 300px;
    z-index: ${theme.star.zIndexGlobal.base};
    &:first-of-type {
        margin-left: 0;
    }
`);

export const LazyImageItem = withConfig(theme => styled(LazyImage, { label: 'LazyImageItem' })`
    inset: 0;
    z-index: ${theme.star.zIndexGlobal.below};
`);

export const HomePageCarouselBettingItem = styled(HomePageCarouselSingleItem, { label: 'HomePageCarouselBettingItem' })`
    align-items: flex-start;
    justify-content: flex-end;
`;

export const CardBodyWrapper = styled('div', { label: 'CardBodyWrapper' })`
    display: flex;
    flex-flow: column nowrap;
    flex: 1 0 0%;
    height: 100%;
    justify-content: center;
    overflow: hidden;
    padding: 8px 72px 0 16px;
    white-space: pre-line;
`;

export const CardBodyTitle = withConfig(theme => styled('h2', { label: 'CardBodyTitle' })`
    ${isNewDesign() ? null : "font-family: Gentona, sans-serif"};
    color: ${theme.star.homePageCarousel.items.txtColor};
    font-size: ${theme.star.fontSize.xMedium};
    font-weight: ${theme.star.fontWeight.bold};
    line-height: 1;
    margin: 0;
    text-transform: uppercase;
`);

export const CardBodySubTitle = withConfig(theme => styled('p', { label: 'CardBodySubTitle' })`
    ${isNewDesign() ? null : "font-family: Gentona, sans-serif"};
    color: ${theme.star.homePageCarousel.items.txtColor};
    font-size: ${theme.star.fontSize.xRegular};
    font-weight: ${theme.star.fontWeight.xMedium};
    line-height: 1.2;
    margin: 4px 0 0;
`);

export const CardButtonWrapper = styled('div', { label: 'CardButtonWrapper' })<{isOptedIn?: boolean | null}>`
    margin-bottom: 16px;
    padding: ${({ isOptedIn }): string => isOptedIn === true ? '0' : '0 16px'};
`;

type CardButtonType = Pick<HomePageCarouselPromoPropsType, 'btnColor'>;

export const CardButton = withConfig(theme => styled(Button, { label: 'CardButton' })<CardButtonType>`
    display: block;
    ${({ btnColor }): string => btnColor === undefined ? '' : `background-color: ${btnColor}`};
    ${theme.star.buttons.tertiary.dark.txtColorCarousel === '' ? '' : `color: ${theme.star.buttons.tertiary.dark.txtColorCarousel}`};
`);

export const OptedInBadgeWrapper = withConfig(theme => styled('div', { label: 'OptedInBadgeWrapper' })<{variant: ButtonVariantType}>`
    display: flex;
    margin-left: 16px;
    margin-bottom: 16px;
    background-color: ${theme.star.buttons.primary.bgColorSecondary};
    padding: 4px 8px;
    color: ${theme.star.badge.txtColorSecondary};
    font-weight: ${theme.star.fontWeight.bold};
    font-size: ${theme.star.fontSize.small};
    line-height: 1.3334;
    cursor: default;
    user-select: none;
    ${({ variant }): SerializedStyles => setOptedInBadgeWrapper(variant, theme)};
`);

export const OptInButtonComponentWrapper = withConfig(theme => styled('div', { label: 'OptInButtonComponentWrapper' })`
    display: flex;
    gap: ${theme.star.gap.small};
`);

export const CheckIcon = withConfig(theme => styled(CheckCircleFilledIcon, { label: 'CheckIcon' })`
    width: 13px;
    margin-right: ${theme.star.gap.small};
    
    & path {
        fill: ${theme.star.card.bgColor};
    }
`);

export const CardBettingWrapper = styled('div', { label: 'CardBettingWrapper' })`
    align-items: flex-start;
    cursor: pointer;
    display: flex;
    flex-flow: column nowrap;
    flex: 1 1 0%;
    justify-content: flex-end;
    padding: 0 16px;
    white-space: pre-line;
    width: 100%;
`;

export const CardBodyActionLink = styled('div', { label: 'CardBodyActionLink' })`
    cursor: pointer;
    display: flex;
    flex-direction: column;
    height: 100%;
    text-decoration: none;
`;

type CardBodyActionWrapperType = Pick<HomePageCarouselPromoPropsType, 'isClickable'>;

export const CardBodyActionWrapper = styled('div', { label: 'CardBodyActionWrapper' })<CardBodyActionWrapperType>`
    cursor: ${({ isClickable = false }): string => isClickable ? 'pointer' : 'default'};
    display: flex;
    flex-direction: column;
    height: 100%;
`;

export const CardFooterWrapper = withConfig(theme => styled('div', { label: 'CardFooterWrapper' })`
    display: flex;
    align-items: center;
    gap: ${theme.star.gap.medium};
    background-color: ${theme.star.homePageCarousel.items.bgColorSecondary};
    color: ${theme.star.homePageCarousel.items.txtColorSecondary};
    flex: 0 0 28px;
    font-size: ${theme.star.fontSize.xSmall};
    line-height: 1.2;
    padding: 8px 16px;
    width: 100%;
`);

export const CardFooterText = styled('div', { label: 'CardFooterText' })`
    > * {
        margin: 0;
    }
`;

export const TcApplied = styled('div', { label: 'TcApplied' })`
    cursor: pointer;
`;

const carouselScrollButtonStyle = (theme: EmotionTheme): SerializedStyles => {
    return css`
        // background-color: ${theme.star.homePageCarousel.controls.bgColor};
        bottom: 0;
        margin: auto 0;
        top: 0;
        @media ${theme.star.mediaQuery.tablet} {
            bottom: 40px;
        }
    `;
};

export const ScrollLeft = withConfig(theme => styled(ScrollLeftButton, { label: 'ScrollLeft' })`
    ${carouselScrollButtonStyle(theme)};
    z-index: initial;
    left: 16px;
`);

export const ScrollRight = withConfig(theme => styled(ScrollRightButton, { label: 'ScrollRight' })`
    ${carouselScrollButtonStyle(theme)};
    right: 16px;
`);

export const ChevronIcon = withConfig(theme => styled(ButtonChevronIcon, { label: 'ChevronIcon' })`
    // fill: ${theme.star.homePageCarousel.controls.bgColorSecondary};
`);

type SelectionsWrapperType = Pick<HomePageCarouselPromoPropsType, 'isOneElem'>;

export const SelectionsWrapper = styled('div', { label: 'SelectionsWrapper' })<SelectionsWrapperType>`
    display: flex;
    padding: 16px;
    width: ${({ isOneElem }): string => isOneElem === true ? 'auto' : '100%'};
`;

export type SelectionPromoWrapperType = Pick<HomePageCarouselPromoPropsType, 'isSelected' | 'isSuspended' | 'itemsInRow'> & { selectionPromoVariant: boolean };

// !TODO: Refactor "isPricedUp" flag - there is a problem with color configuration for hero carousel for PricedUp
export const SelectionPromoWrapper = withConfig(theme => styled('div', { label: 'SelectionPromoWrapper' })<SelectionPromoWrapperType>`
    align-items: center;
    background-color: ${(props): string => getSelectionPromoBgColor(theme, props)};
    color: ${(props): string => getSelectionPromoTextColor(theme, props)};
    cursor: pointer;
    display: flex;
    flex: ${({ itemsInRow }): string => itemsInRow === 2 ? '1 1 0%' : '0 0 0%'};
    justify-content: space-between;
    margin-left: 4px;
    min-width: 86px;
    overflow: hidden;
    padding: ${({ itemsInRow }): string => itemsInRow === 1 ? '0' : '8px'};
    text-overflow: ellipsis;
    &:first-of-type {
        margin-left: 0;
    }
`);

// !TODO: Refactor "selectionPromoVariant" flag - there is a problem with color configuration for hero carousel for PricedUp and LondonBet
export const SelectionText = withConfig(theme => styled('div', { label: 'SelectionText' })<{isSelected: boolean; selectionPromoVariant: boolean}>`
    color: ${({ isSelected, selectionPromoVariant }): string => selectionPromoVariant ? isSelected ? theme.star.selection.txtColorQuinary : theme.star.selection.bgColor : theme.star.homePageCarousel.items.txtColorTertiary};
    flex: 1 1 auto;
    font-size: ${theme.star.fontSize.xSmall};
    font-weight: ${({ selectionPromoVariant }): string => selectionPromoVariant ? '600' : '400'};
    line-height: 1.4;
    overflow: hidden;
    padding-right: 6px;
    text-overflow: ellipsis;
    white-space: nowrap;
`);

export type SelectionPriceType = Pick<HomePageCarouselPromoPropsType, 'isSelected' | 'isSuspended' | 'isOneElem'> & { selectionPromoVariant: boolean };

// !TODO: Refactor "selectionPromoVariant" flag - there is a problem with color configuration for hero carousel for PricedUp and LondonBet
export const SelectionPrice = withConfig(theme => styled('div', { label: 'SelectionPrice' })<SelectionPriceType>`
    ${({ isOneElem }): string => isOneElem === true ? 'min-width: 87px' : ''};
    background-color: ${(props): string => getSelectionPromoBgColor(theme, props)};
    color: ${(props): string => getSelectionPromoTextColor(theme, props)};
    cursor: pointer;
    font-size: ${theme.star.fontSize.small};
    font-weight: ${theme.star.fontWeight.bold};
    height: 100%;
    line-height: 1.3334;
    padding: ${({ isOneElem }): string => isOneElem === true ? '8px 16px' : '0'};
    text-align: center;
    &:only-of-type {
        width: 100%;
    }
`);
